import cn from 'clsx';
import { faAngleRight } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import CategoryIcon from './CategoryIcon';
import { Category } from './types';

type Props = {
  categories: Category[];
  selectedId: string | null;
  onSelect: (categoryId: string | null) => void;
};

/**
 * Magzter's categories, down the left.
 *
 * Buttons rather than links, because picking one is a request for different covers and
 * not a different page — the same reasoning the Reviews pager is built on. Hidden below
 * `md`, where GridHeader's select takes over; two controls for one choice is what the
 * source did and it is the right call, since a column of forty categories on a phone
 * would push the covers off the screen.
 */
export default function CategorySidebar({ categories, selectedId, onSelect }: Props) {
  return (
    <nav aria-label="Magazine categories" className="hidden md:block md:col-span-5 lg:col-span-3">
      <div className="p-3 bg-slate-100 rounded text-center mb-3">
        <h2 className="text-xl font-semibold">Categories</h2>
      </div>

      <ul className="list-none p-0 m-0">
        {categories.map((category) => {
          const isActive = category.id === selectedId;

          return (
            <li key={category.id ?? 'all'}>
              <button
                type="button"
                aria-current={isActive ? 'true' : undefined}
                onClick={() => onSelect(category.id)}
                className={cn(
                  'group flex items-center justify-between font-semibold text-left w-full pl-3 pr-4 py-2 rounded mb-1',
                  isActive
                    ? 'text-black bg-yellow-400'
                    : 'bg-white text-black hover:bg-yellow-100 hover:text-yellow-900 active:bg-yellow-400'
                )}
              >
                <span className="inline-flex items-center gap-3">
                  <CategoryIcon category={category} isActive={isActive} />
                  <span>{category.name}</span>
                </span>
                <FontAwesomeIcon
                  icon={faAngleRight}
                  size="lg"
                  className={cn(
                    'group-hover:text-yellow-900',
                    isActive ? 'text-yellow-900' : 'text-yellow-500'
                  )}
                />
              </button>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
